/**
 * Header component for show details page with inline editing
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link as MuiLink,
  Chip,
  Stack,
  TextField,
  MenuItem,
  Button,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Home as HomeIcon,
  Event as EventIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { Show, ShowTypeEnum } from 'api-client/models';
import { SHOW_STATUS_LABELS, SHOW_STATUS_COLORS, SHOW_TYPE_LABELS } from '../../utils/constants';
import { isShowInFuture } from '../../utils/validators';

interface ShowDetailsHeaderProps {
  show: Show;
  onUpdate: (data: {
    title?: string;
    showType?: ShowTypeEnum;
    performerDealType?: string | null;
    doorDealPercentageOwner?: string | null;
    doorDealPercentage?: string | null;
  }) => Promise<void>;
}

const SHOW_TYPE_OPTIONS: Array<{ value: ShowTypeEnum; label: string }> = [
  { value: 'concert' as ShowTypeEnum, label: 'Concert' },
  { value: 'festival' as ShowTypeEnum, label: 'Festival' },
  { value: 'comedy' as ShowTypeEnum, label: 'Comedy Show' },
  { value: 'theater' as ShowTypeEnum, label: 'Theater' },
  { value: 'dance' as ShowTypeEnum, label: 'Dance Performance' },
  { value: 'spoken_word' as ShowTypeEnum, label: 'Spoken Word' },
  { value: 'private_event' as ShowTypeEnum, label: 'Private Event' },
  { value: 'other' as ShowTypeEnum, label: 'Other' },
];

const DEAL_TYPE_OPTIONS = [
  { value: 'guarantee', label: 'Guarantee' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'guarantee_plus', label: 'Guarantee + Percentage' },
  { value: 'door_deal', label: 'Door Deal' },
  { value: 'comp', label: 'Comp (Free)' },
  { value: 'other', label: 'Other / Negotiated' },
];

const DOOR_DEAL_OWNER_OPTIONS = [
  { value: 'house', label: 'House' },
  { value: 'promoter', label: 'Promoter' },
  { value: 'house_is_promoter', label: 'House (also promoter)' },
  { value: 'custom', label: 'Custom / Negotiated' },
];

export const ShowDetailsHeader: React.FC<ShowDetailsHeaderProps> = ({
  show,
  onUpdate,
}) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState({
    title: show.title,
    showType: show.showType,
    performerDealType: show.performerDealType || '',
    doorDealPercentageOwner: show.doorDealPercentageOwner || '',
    doorDealPercentage: show.doorDealPercentage || '',
  });

  const canEdit = isShowInFuture(show);

  const handleStartEdit = () => {
    if (!canEdit) return;
    setError(null);
    setEditFormData({
      title: show.title,
      showType: show.showType,
      performerDealType: show.performerDealType || '',
      doorDealPercentageOwner: show.doorDealPercentageOwner || '',
      doorDealPercentage: show.doorDealPercentage || '',
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await onUpdate({
        title: editFormData.title,
        showType: editFormData.showType,
        performerDealType: editFormData.performerDealType || null,
        doorDealPercentageOwner:
          editFormData.performerDealType === 'door_deal'
            ? editFormData.doorDealPercentageOwner || null
            : null,
        doorDealPercentage:
          editFormData.performerDealType === 'door_deal' &&
          editFormData.doorDealPercentage !== ''
            ? editFormData.doorDealPercentage
            : null,
      });
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update show.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  return (
    <>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: { xs: 1, sm: 2 }, '& .MuiBreadcrumbs-li:last-child': { display: { xs: 'none', sm: 'block' } } }}>
        <MuiLink
          component="button"
          onClick={() => navigate('/')}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          underline="hover"
          color="inherit"
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </MuiLink>
        <MuiLink
          component="button"
          onClick={() => navigate('/shows')}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          underline="hover"
          color="inherit"
        >
          <EventIcon sx={{ mr: 0.5 }} fontSize="small" />
          Shows
        </MuiLink>
        <Typography
          color="text.primary"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          {show.title}
        </Typography>
      </Breadcrumbs>

      {/* Show Info - Edit or View Mode */}
      {isEditing ? (
        <Box sx={{ mb: 3 }}>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Show Title"
              disabled={saving}
              value={editFormData.title || ''}
              onChange={(e) =>
                setEditFormData({ ...editFormData, title: e.target.value })
              }
              fullWidth
              required
              autoFocus
            />
            <TextField
              label="Show Type"
              disabled={saving}
              value={editFormData.showType || ''}
              onChange={(e) =>
                setEditFormData({
                  ...editFormData,
                  showType: e.target.value as ShowTypeEnum,
                })
              }
              select
              fullWidth
            >
              {SHOW_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Performer Deal Type (Show-Level)"
              disabled={saving}
              value={editFormData.performerDealType || ''}
              onChange={(e) =>
                setEditFormData({
                  ...editFormData,
                  performerDealType: e.target.value,
                  ...(e.target.value !== 'door_deal'
                    ? { doorDealPercentageOwner: '', doorDealPercentage: '' }
                    : {}),
                })
              }
              select
              fullWidth
              helperText="Newly added/edited performers inherit this deal type."
            >
              <MenuItem value="">Not set</MenuItem>
              {DEAL_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            {editFormData.performerDealType === 'door_deal' && (
              <>
                <Alert severity="info" variant="outlined">
                  Door deal values are optional and negotiable. This sets default split context for the show.
                </Alert>
                <TextField
                  label="Door Deal Percentage Belongs To"
                  disabled={saving}
                  value={editFormData.doorDealPercentageOwner || ''}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      doorDealPercentageOwner: e.target.value,
                    })
                  }
                  select
                  fullWidth
                >
                  <MenuItem value="">Not set</MenuItem>
                  {DOOR_DEAL_OWNER_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Door Deal Percentage (%)"
                  disabled={saving}
                  type="number"
                  value={editFormData.doorDealPercentage || ''}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      doorDealPercentage: e.target.value,
                    })
                  }
                  fullWidth
                  inputProps={{ step: '0.01', min: '0', max: '100' }}
                  helperText="Example: 30 means 30% for the selected owner; the remaining % is for others by agreement."
                />
              </>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !editFormData.title}
                startIcon={<SaveIcon />}
              >
                Save Changes
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={saving}
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Box>
      ) : (
        <Box
          onClick={canEdit ? handleStartEdit : undefined}
          sx={{
            mb: { xs: 2, sm: 3 },
            cursor: canEdit ? 'pointer' : 'default',
            '&:hover': canEdit
              ? {
                  backgroundColor: 'action.hover',
                  borderRadius: 1,
                }
              : {},
            padding: 2,
            marginLeft: -2,
            marginTop: -1,
          }}
        >
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }} gutterBottom>
            {show.title}
          </Typography>
          <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1, '& .MuiChip-label': { whiteSpace: 'normal' }, '& .MuiChip-root': { height: 'auto', minHeight: 32, maxWidth: '100%' } }} alignItems="center">
            <Chip
              label={SHOW_STATUS_LABELS[show.status || ''] || show.status}
              color={SHOW_STATUS_COLORS[show.status || ''] || 'default'}
            />
            {show.showType && (
              <Chip
                label={SHOW_TYPE_LABELS[show.showType] || show.showType}
                variant="outlined"
              />
            )}
            {show.performerDealType && (
              <Chip
                label={`Performer Deal: ${DEAL_TYPE_OPTIONS.find((option) => option.value === show.performerDealType)?.label || show.performerDealType}`}
                variant="outlined"
              />
            )}
            {show.isSettled && (
              <Chip label="Settled" color="success" variant="outlined" />
            )}
            {canEdit && (
              <IconButton aria-label="Edit show details" size="small"
                onClick={(event) => { event.stopPropagation(); handleStartEdit(); }}>
                <EditIcon sx={{ color: 'action.active', fontSize: '1.2rem' }} />
              </IconButton>
            )}
          </Stack>
        </Box>
      )}

      {show.performerDealType === 'door_deal' &&
        (show.doorDealPercentageOwner || show.doorDealPercentage) && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            Door Deal Context: {
              show.doorDealPercentageOwner
                ? DOOR_DEAL_OWNER_OPTIONS.find((option) => option.value === show.doorDealPercentageOwner)?.label || show.doorDealPercentageOwner
                : 'Owner not set'
            }
            {show.doorDealPercentage ? ` @ ${show.doorDealPercentage}%` : ''}
          </Alert>
        )}
    </>
  );
};
